/**
 * @file     FlowHeading.tsx
 * @readme   ../README.md
 * @purpose  One question per step, rendered as a real heading at the level the page allows (h2 by default, h3 inside a section), with its optional helper line.
 */
'use client';

import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useFlowPresentation, type FlowHeadingLevel } from './FlowPresentation';
import { flowColors } from './flowKitTokens';

type FlowHeadingProps = {
  children: ReactNode;
  subtext?: ReactNode;
  id?: string;
  /** Overrides the level the FlowRenderer provides. */
  level?: FlowHeadingLevel;
  /** Larger type for a step that is the whole card (deal-finder questions). */
  size?: 'md' | 'lg';
};

export const FlowHeading = ({ children, subtext, id, level, size = 'md' }: FlowHeadingProps) => {
  const presentation = useFlowPresentation();
  const component = level ?? presentation.headingLevel;
  const compact = presentation.density === 'compact';
  const fontSize = size === 'lg'
    ? { xs: 24, sm: 28 }
    : compact
      ? { xs: 19, sm: 20 }
      : { xs: 20, sm: 23 };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography
        component={component}
        id={id}
        sx={{
          m: 0,
          fontSize,
          lineHeight: 1.25,
          fontWeight: 600,
          letterSpacing: '-0.015em',
          color: flowColors.textPrimary,
          textWrap: 'pretty',
        }}
      >
        {children}
      </Typography>
      {subtext ? (
        <Typography
          component="p"
          sx={{ m: 0, fontSize: 15, lineHeight: 1.55, color: flowColors.textSecondary, textWrap: 'pretty' }}
        >
          {subtext}
        </Typography>
      ) : null}
    </Box>
  );
};
